// ─────────────────────────────────────────────────────────────────
// Glass-i — Contra Voucher API client (Stage 6B.9C).
//
// Thin axios wrappers around /api/erp/accounts/contras/*. The
// Approval-MFA challenge id flows through the standard
// `x-approval-mfa-challenge-id` header on post / cancel / delete
// (Contra is the first module to gate draft delete with MFA — see
// Stage 6B.9A §8 hardening item #1).
//
// The cash/bank ledger lookup endpoint is shared with Payment /
// Receipt (mounted at /ledgers/cash-bank by Stage 6B.6) so we re-use
// it directly — no separate contra-specific lookup.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';
import { translateQuery } from './contraVoucherHelpers';

const BASE = '/api/erp/accounts';
const CONTRAS = `${BASE}/contras`;

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const contraVoucherApi = {
  list: (query) =>
    client.post(`${CONTRAS}/list`, translateQuery(query) || {}).then(unwrap),
  get: (id) =>
    client.get(`${CONTRAS}/${encodeURIComponent(id)}`).then(unwrap),
  createDraft: (payload) =>
    client.post(CONTRAS, payload).then(unwrap),
  updateDraft: (id, payload) =>
    client.put(`${CONTRAS}/${encodeURIComponent(id)}`, payload).then(unwrap),
  post: (id, { approvalMfaChallengeId } = {}) =>
    client.post(`${CONTRAS}/${encodeURIComponent(id)}/post`, {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  cancel: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${CONTRAS}/${encodeURIComponent(id)}/cancel`, body || {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${CONTRAS}/${encodeURIComponent(id)}`, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),

  // ── Cash/Bank ledger lookup ──
  // Stage 6B.9B retrofit: prefer the canonical subtype lookup
  // (`/ledgers/lookups/by-subtype?subtype=bank,cash`); fall back to
  // the legacy `/ledgers/cash-bank` endpoint when the new one fails
  // (network error / earlier backend / not authorised). The fallback
  // is silent so the modal stays functional during a partial upgrade.
  cashBankLookup: async (q, limit = 100) => {
    const params = { subtype: 'bank,cash', ...(q ? { q } : {}), limit };
    try {
      const resp = await client.get(`${BASE}/ledgers/lookups/by-subtype`, { params });
      return unwrap(resp);
    } catch (_e) {
      // Legacy fallback — backend has not been migrated yet, or
      // tenant ledgers haven't been classified. The account_group
      // ILIKE filter still works for the historical seed pattern.
      const resp = await client.get(`${BASE}/ledgers/cash-bank`, {
        params: { ...(q ? { q } : {}), limit }
      });
      return unwrap(resp);
    }
  }
};

export default contraVoucherApi;
